import {
  Box,
  Collapse,
  IconButton,
  List,
  Typography,
  useTheme,
} from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from '@material-ui/icons/Warning';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { PortHealthAlert } from '../../../stores/PortHealthStore';
import { AlertSection } from './AlertSection';
import {
  BANNER_HEIGHT,
  getSeverityColor,
  getSeverityFromValue,
  PortHealthSeverity,
  processPortHealthAlerts,
} from './utils';

interface PortHealthBannerProps {
  portHealthAlerts: PortHealthAlert[];
}

export const PortHealthBanner: React.FC<PortHealthBannerProps> = ({ portHealthAlerts }) => {
  const intl = useIntl();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const alerts = processPortHealthAlerts(portHealthAlerts);

  const criticalAlerts = alerts.filter(
    (a) => getSeverityFromValue(a.value) === PortHealthSeverity.Critical,
  );
  const warningAlerts = alerts.filter(
    (a) => getSeverityFromValue(a.value) === PortHealthSeverity.Warning,
  );

  useEffect(() => {
    if (alerts.length > 0) {
      document.documentElement.style.setProperty('--port-health-banner-height', `${BANNER_HEIGHT}px`);
    } else {
      document.documentElement.style.setProperty('--port-health-banner-height', '0px');
    }
    return () => {
      document.documentElement.style.setProperty('--port-health-banner-height', '0px');
    };
  }, [alerts.length]);

  if (alerts.length === 0) return null;

  const bannerColor = getSeverityColor(alerts[0]?.value);

  return (
    <Box style={{ backgroundColor: bannerColor, color: 'white' }}>
      {/* Summary row — in normal flow, pushes content down */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ height: BANNER_HEIGHT, cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ErrorOutlineIcon style={{ marginRight: 8 }} />
        <Typography style={{ fontWeight: 'bold' }}>
          {alerts.length}{' '}
          {intl.formatMessage({
            id: 'portHealth.banner.alertCount',
            defaultMessage: 'Port Health Alert(s)',
          })}
        </Typography>
        <IconButton size="small" style={{ color: 'white', marginLeft: 8 }}>
          <ExpandMoreIcon
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s',
            }}
          />
        </IconButton>
      </Box>

      {/* Expanded alert list — positioned absolute to overlay content */}
      <div style={{ position: 'relative' }}>
        <Collapse in={isExpanded}>
          <div style={{ position: 'absolute', left: 0, right: 0, zIndex: theme.zIndex.appBar }}>
            <List dense style={{ backgroundColor: bannerColor, padding: 0 }}>
              <AlertSection
                alerts={criticalAlerts}
                label={intl.formatMessage({
                  id: 'portHealth.banner.critical',
                  defaultMessage: 'CRITICAL',
                })}
                icon={ErrorOutlineIcon}
              />
              <AlertSection
                alerts={warningAlerts}
                label={intl.formatMessage({
                  id: 'portHealth.banner.warning',
                  defaultMessage: 'WARNING',
                })}
                icon={WarningIcon}
                bgcolor="#ff9800"
              />
            </List>
          </div>
        </Collapse>
      </div>
    </Box>
  );
};
